import { useRef, useState } from "react";
import { Download, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, downloadFile } from "@/lib/api";

const STATUS = {
  new: ["Will be added", "bg-emerald-50 text-emerald-700"],
  exists: ["Skipped — exists", "bg-amber-50 text-amber-700"],
  error: ["Error", "bg-red-50 text-red-700"],
};

const PreviewTable = ({ rows }) => (
  <div className="max-h-[50vh] overflow-auto rounded border border-border">
    <table className="data-table text-xs" data-testid="asin-upload-preview-table">
      <thead><tr><th>Row</th><th>ASIN</th><th>Seller SKU</th><th>Group</th><th className="min-w-[200px]">Contents</th><th>Result</th><th className="min-w-[200px]">Note</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.row} data-testid={`asin-upload-row-${r.row}`}>
            <td className="num">{r.row}</td><td className="font-mono">{r.asin || "—"}</td><td>{r.seller_sku}</td><td>{r.group}</td>
            <td className="wrap-cell">{r.contents || "—"}</td>
            <td><span className={`rounded px-1.5 py-0.5 font-semibold ${STATUS[r.status][1]}`} data-testid={`asin-upload-status-${r.row}`}>{STATUS[r.status][0]}</span></td>
            <td className="wrap-cell text-muted-foreground">{r.message}</td>
          </tr>))}
      </tbody>
    </table>
  </div>
);

export const AsinUploadDialog = ({ open, onOpenChange, onSaved }) => {
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const close = (o) => { if (!o) setPrev(null); onOpenChange(o); };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { setPrev((await api.post("/asins-import/preview", fd)).data); } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/asins-import/commit", { token: prev.token })).data;
      toast.success(`${r.added} ASIN${r.added === 1 ? "" : "s"} added${r.skipped.length ? `, ${r.skipped.length} skipped (already existed)` : ""}`);
      onSaved(); close(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const template = () => downloadFile("/asins-import/template", {}, "asin_upload_template.xlsx").catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-5xl" data-testid="asin-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload new ASINs</DialogTitle>
          <DialogDescription>Download the template, fill one row per new ASIN (products by Product Master SKU), then upload it. You'll see a preview before anything is saved. ASINs already in the catalogue are skipped.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={template} data-testid="asin-template-download-btn"><Download className="h-4 w-4" /> Download template</Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="asin-upload-file-input" />
          <Button size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="asin-upload-choose-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {prev ? "Upload another file" : "Choose filled sheet"}
          </Button>
        </div>
        {prev && <div className="space-y-2" data-testid="asin-upload-preview">
          <div className="text-sm" data-testid="asin-upload-summary"><b className="text-emerald-700">{prev.new}</b> will be added · <b className="text-amber-700">{prev.exists}</b> skipped (already exist) · <b className="text-red-600">{prev.errors}</b> with errors{prev.errors > 0 && " — fix them in the sheet and upload again, or save the rest now"}</div>
          <PreviewTable rows={prev.rows} />
        </div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid="asin-upload-cancel-btn">Cancel</Button>
          <Button onClick={commit} disabled={busy || !prev?.token} data-testid="asin-upload-confirm-btn">Add {prev?.new || 0} ASIN{prev?.new === 1 ? "" : "s"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
